import React, { useState, useEffect, createContext, useContext, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Link, Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { auth, db } from './firebase.js'; 
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

// --- NEW STRIPE IMPORTS ---
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// --- Lazy Load Page Components ---
const HomePage = lazy(() => import('./pages/HomePage'));
const PartiesPage = lazy(() => import('./pages/PartiesPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// --- NEW: Load Stripe with your publishable key (must be in .env) ---
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// --- Authentication Context (Unchanged) ---
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setUser(user);
            if (user) {
                const userDocRef = doc(db, "users", user.uid);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    setUserData(userDoc.data());
                }
            } else {
                setUserData(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // --- MODIFIED: Pass userData to the context value ---
    return (
        <AuthContext.Provider value={{ user, userData, loading }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

// --- Layout & Route Components (Unchanged) ---
const Header = React.memo(() => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { user, userData } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        signOut(auth).then(() => navigate('/')).catch(console.error);
    };

    const handleScroll = (hash) => {
        setIsMenuOpen(false);
        if (location.pathname === '/') {
            const el = document.getElementById(hash.replace('#', ''));
            if (el) el.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate(`/${hash}`);
        }
    };

    return (
        <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
            <div className="container mx-auto px-6 py-3 flex justify-between items-center">
                <Link to="/"><img src="/images/logo.png" alt="Little Gym Stars Logo" className="h-12 w-auto" /></Link>
                <nav className="hidden md:flex items-center space-x-8">
                    <button onClick={() => handleScroll('#about')} className="text-gray-600 hover:text-orange-600">About</button>
                    <button onClick={() => handleScroll('#classes')} className="text-gray-600 hover:text-orange-600">Classes</button>
                    <Link to="/parties" className="text-gray-600 hover:text-orange-600">Parties</Link>
                    {user ? (
                        <>
                            <Link to="/dashboard" className="text-gray-600 hover:text-orange-600">Dashboard</Link>
                            {userData?.isAdmin && <Link to="/admin" className="text-orange-700 font-bold">Admin</Link>}
                            <button onClick={handleLogout} className="text-gray-600 hover:text-orange-600">Logout</button>
                        </>
                    ) : (
                        <Link to="/account" className="text-gray-600 hover:text-orange-600">Account</Link>
                    )}
                </nav>
                <button onClick={() => handleScroll('#contact')} className="hidden md:inline-block bg-orange-600 text-white font-semibold px-5 py-2 rounded-lg hover:bg-orange-700">Enroll Now</button>
                <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path></svg></button>
            </div>
            {isMenuOpen && (
                <div className="md:hidden px-6 pb-4" onClick={() => setIsMenuOpen(false)}>
                    <button onClick={() => handleScroll('#about')} className="block py-2">About</button>
                    <button onClick={() => handleScroll('#classes')} className="block py-2">Classes</button>
                    <Link to="/parties" className="block py-2">Parties</Link>
                    {user ? (
                        <>
                            <Link to="/dashboard" className="block py-2">Dashboard</Link>
                            {userData?.isAdmin && <Link to="/admin" className="block py-2 font-bold text-orange-700">Admin</Link>}
                            <button onClick={handleLogout} className="block py-2 text-left w-full">Logout</button>
                        </>
                    ) : (
                        <Link to="/account" className="block py-2">Account</Link>
                    )}
                    <button onClick={() => handleScroll('#contact')} className="block py-2">Contact</button>
                </div>
            )}
        </header>
    );
});

const Footer = React.memo(() => (
    <footer className="bg-gray-800 text-white mt-auto">
        <div className="container mx-auto px-6 py-8 text-center">
            <p>&copy; {new Date().getFullYear()} Little Gym Stars. All Rights Reserved.</p>
        </div>
    </footer>
));

const Layout = () => (
    <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow"><Outlet /></main>
        <Footer />
    </div>
);

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return <div>Loading...</div>;
    return user ? children : <Navigate to="/account" />;
};

const AdminRoute = ({ children }) => {
    const { user, userData, loading } = useAuth();
    if (loading) return <div>Loading...</div>;
    return user && userData?.isAdmin ? children : <Navigate to="/dashboard" />;
};

// --- Main App Component ---
export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                {/* --- MODIFIED: Added Stripe Elements Wrapper --- */}
                <Elements stripe={stripePromise}>
                    <Suspense fallback={<div className="text-center py-40 font-bold text-lg">Loading...</div>}>
                        <Routes>
                            <Route path="/" element={<Layout />}>
                                <Route index element={<HomePage />} />
                                <Route path="parties" element={<PartiesPage />} />
                                <Route path="account" element={<AccountPage />} />
                                <Route path="dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                                <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
                                <Route path="*" element={<NotFoundPage />} />
                            </Route>
                        </Routes>
                    </Suspense>
                </Elements>
            </AuthProvider>
        </BrowserRouter>
    );
}